/**
 * Iter 428.p7 — Planning & Reports list for a project.
 *
 * Renders BELOW the specialised ProjectPlannerCard (which stays pinned
 * at the top and drives the proposal-kind draft's brainstorm/plan/
 * estimate rail). This list surfaces EVERY workbench draft attached
 * to the project — proposal, planning, and any report kind — as click-
 * through rows, with a kind chip filter at the top:
 *
 *   All  |  Proposal  |  Planning  |  Reports ▾  [+ New session]
 *
 * The Reports chip is a dropdown that reveals per-report-kind sub-
 * filters (pump assessment / technical / engineering note / inspection
 * / financial breakdown / general / custom:*). Click a row to open
 * the full Workbench editor for that draft.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Plus, FileText, Sparkles, ArrowRight, ChevronDown,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import {
  WORKBENCH_KINDS, labelForKind, groupForKind, normaliseCustomKind,
} from "../../lib/workbenchKinds";

const STAGE_LABEL = {
  brainstorm: "Brainstorm", plan: "Plan", estimate: "Estimate", promoted: "Promoted",
};

// The order chips appear across the top of the tab.
const TOP_FILTERS = [
  { key: "all",      label: "All",      match: () => true },
  { key: "proposal", label: "Proposal", match: (k) => groupForKind(k) === "proposal" },
  { key: "planning", label: "Planning", match: (k) => groupForKind(k) === "planning" },
  { key: "reports",  label: "Reports",  match: (k) => groupForKind(k) === "reports"  },
];

const REPORT_KINDS = WORKBENCH_KINDS.filter((k) => k.group === "reports");

export default function ProjectPlanningSessions({ projectId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [newKind, setNewKind] = useState("planning");
  const [customKind, setCustomKind] = useState("");
  const [filter, setFilter] = useState("all");
  const [reportSubFilter, setReportSubFilter] = useState(null);   // e.g. "pump_assessment" or null (any)
  const [reportChipOpen, setReportChipOpen] = useState(false);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workbench/drafts/all-by-project/${projectId}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load workbench sessions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  // ── Apply chip filter + sub-filter to the loaded rows ─────────
  const filtered = useMemo(() => {
    const top = TOP_FILTERS.find((f) => f.key === filter) || TOP_FILTERS[0];
    let out = items.filter((d) => top.match(d.kind || "proposal"));
    if (filter === "reports" && reportSubFilter) {
      out = out.filter((d) => (d.kind || "") === reportSubFilter);
    }
    return out;
  }, [items, filter, reportSubFilter]);

  const counts = useMemo(() => {
    const c = { all: items.length, proposal: 0, planning: 0, reports: 0 };
    for (const d of items) {
      const g = groupForKind(d.kind || "proposal");
      if (g === "proposal")     c.proposal++;
      else if (g === "planning") c.planning++;
      else                       c.reports++;
    }
    return c;
  }, [items]);

  const createSession = async () => {
    if (!title.trim()) { toast.error("Give the session a title."); return; }
    let kindOnWire = newKind;
    if (newKind === "__custom__") {
      const norm = normaliseCustomKind(customKind);
      if (!norm) {
        toast.error("Enter a custom kind (letters + underscores).");
        return;
      }
      kindOnWire = norm;
    }
    setCreating(true);
    try {
      // Proposals still land through the standard get-or-create; every
      // other kind lands via /planning-sessions.
      const path = kindOnWire === "proposal"
        ? `/workbench/drafts`
        : `/projects/${projectId}/planning-sessions`;
      const body = kindOnWire === "proposal"
        ? { name: title.trim(), project_id: projectId, kind: "proposal" }
        : { title: title.trim(), kind: kindOnWire };
      const { data } = await api.post(path, body);
      toast.success("Session created.");
      navigate(`/projects/workbench/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create session.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="hv-card p-3 mt-4" data-testid="project-planning-sessions">
      {/* Header: title + New session button */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-fuchsia-300" />
          <div className="font-display font-bold text-sm">All Workbench sessions</div>
          <span className="text-[10px] text-[#647087]">
            (proposal, planning, and every report kind)
          </span>
        </div>
        <button
          onClick={() => setComposerOpen((v) => !v)}
          className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
          data-testid="project-planning-new-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New session
        </button>
      </div>

      {/* Filter chip strip */}
      <div className="flex gap-1 flex-wrap mb-3">
        {TOP_FILTERS.map((f) => {
          const active = filter === f.key;
          const isReportChip = f.key === "reports";
          return (
            <div key={f.key} className="relative">
              <button
                onClick={() => {
                  setFilter(f.key);
                  if (!isReportChip) { setReportChipOpen(false); setReportSubFilter(null); }
                  else setReportChipOpen((v) => !v);
                }}
                className={`text-[11px] px-2.5 py-1 rounded border inline-flex items-center gap-1 transition ${
                  active
                    ? "border-fuchsia-400 bg-fuchsia-500/10 text-fuchsia-200"
                    : "border-white/10 text-[#cbd2df] hover:border-fuchsia-400/40"
                }`}
                data-testid={`project-planning-filter-${f.key}`}
              >
                {f.label}
                <span className="text-[9px] text-[#647087]">({counts[f.key]})</span>
                {isReportChip && <ChevronDown className="w-3 h-3 opacity-60" />}
              </button>
              {isReportChip && reportChipOpen && (
                <div
                  className="absolute z-30 mt-1 left-0 min-w-[200px] rounded-md border border-white/10 bg-[#0f172a] shadow-lg p-1"
                  data-testid="project-planning-report-sub-filter"
                >
                  <button
                    onClick={() => { setReportSubFilter(null); setReportChipOpen(false); }}
                    className={`w-full text-left text-[11px] px-2 py-1 rounded ${
                      reportSubFilter === null ? "bg-fuchsia-500/15 text-fuchsia-200" : "hover:bg-white/5 text-[#cbd2df]"
                    }`}
                    data-testid="project-planning-report-sub-any"
                  >
                    Any report kind
                  </button>
                  {REPORT_KINDS.map((r) => (
                    <button
                      key={r.key}
                      onClick={() => { setReportSubFilter(r.key); setReportChipOpen(false); }}
                      className={`w-full text-left text-[11px] px-2 py-1 rounded ${
                        reportSubFilter === r.key ? "bg-fuchsia-500/15 text-fuchsia-200" : "hover:bg-white/5 text-[#cbd2df]"
                      }`}
                      data-testid={`project-planning-report-sub-${r.key}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New-session composer */}
      {composerOpen && (
        <div
          className="rounded-md border border-fuchsia-400/30 bg-fuchsia-500/[0.05] p-2 mb-3 space-y-2"
          data-testid="project-planning-new-composer"
        >
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Session title — e.g. Filter train redesign · Feb 25"
            className="hv-input text-sm w-full"
            data-testid="project-planning-new-title"
          />
          <div className="flex gap-1 flex-wrap items-center">
            <span className="text-[10px] text-[#647087] uppercase tracking-wider mr-1">Kind</span>
            {WORKBENCH_KINDS.map((k) => (
              <button
                key={k.key}
                onClick={() => setNewKind(k.key)}
                className={`text-[10px] px-2 py-0.5 rounded border ${
                  newKind === k.key
                    ? "border-fuchsia-400 bg-fuchsia-500/10 text-fuchsia-200"
                    : "border-white/10 text-[#cbd2df] hover:border-fuchsia-400/40"
                }`}
                data-testid={`project-planning-new-kind-${k.key}`}
              >
                {k.label}
              </button>
            ))}
            <button
              onClick={() => setNewKind("__custom__")}
              className={`text-[10px] px-2 py-0.5 rounded border ${
                newKind === "__custom__"
                  ? "border-fuchsia-400 bg-fuchsia-500/10 text-fuchsia-200"
                  : "border-white/10 text-[#cbd2df] hover:border-fuchsia-400/40"
              }`}
              data-testid="project-planning-new-kind-custom"
            >
              + Add other
            </button>
          </div>
          {newKind === "__custom__" && (
            <input
              value={customKind}
              onChange={(e) => setCustomKind(e.target.value)}
              placeholder="Custom kind name (e.g. site_survey)"
              className="hv-input text-sm w-full"
              data-testid="project-planning-new-kind-custom-input"
            />
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setComposerOpen(false); setTitle(""); setCustomKind(""); }}
              disabled={creating}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="project-planning-new-cancel"
            >
              Cancel
            </button>
            <button
              onClick={createSession}
              disabled={creating || !title.trim()}
              className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1"
              data-testid="project-planning-new-submit"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create
            </button>
          </div>
        </div>
      )}

      {/* Rows */}
      {loading ? (
        <div className="p-3 text-xs text-[#a0aabf] inline-flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-[12px] text-[#647087] italic p-2">
          No workbench sessions match this filter yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((d) => {
            const k = d.kind || "proposal";
            const g = groupForKind(k);
            const groupTint =
              g === "proposal" ? "text-cyan-200 border-cyan-400/40 bg-cyan-500/10" :
              g === "planning" ? "text-emerald-200 border-emerald-400/40 bg-emerald-500/10" :
                                 "text-fuchsia-200 border-fuchsia-400/40 bg-fuchsia-500/10";
            return (
              <button
                key={d.id}
                onClick={() => navigate(`/projects/workbench/${d.id}`)}
                className="w-full text-left rounded-md border border-white/10 hover:border-fuchsia-400/40 hover:bg-fuchsia-500/[0.05] p-2 transition flex items-center gap-2"
                data-testid={`project-planning-row-${d.id}`}
              >
                <FileText className="w-4 h-4 text-fuchsia-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-[#cbd2df] truncate">{d.name || "Untitled session"}</div>
                  <div className="text-[10px] text-[#647087] mt-0.5 truncate">
                    {d.current_stage ? `${STAGE_LABEL[d.current_stage] || d.current_stage} · ` : ""}
                    {d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}
                    {d.seeded_from_session_id ? " · seeded from engineering session" : ""}
                  </div>
                </div>
                <span
                  className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border shrink-0 ${groupTint}`}
                >
                  {labelForKind(k)}
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-[#647087] shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
